import { Modal } from '@/components/Modal';
import { createCustomer, searchCustomers } from '@/lib/api';
import type { Customer } from '@/lib/types';
import { ExternalLink, Loader2, Plus, Search, Users, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  return phone;
}

// ---------------------------------------------------------------------------
// Add customer modal
// ---------------------------------------------------------------------------

interface AddCustomerModalProps {
  onClose: () => void;
  onCreated: (customerId: string) => void;
}

function AddCustomerModal({ onClose, onCreated }: AddCustomerModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!firstName.trim() || !lastName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const customer = await createCustomer({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
      });
      onCreated(customer.id);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md">
      <form
        className="p-6 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">Add customer</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary press"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="add-customer-first"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              First name
            </label>
            <input
              id="add-customer-first"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoFocus
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div>
            <label
              htmlFor="add-customer-last"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Last name
            </label>
            <input
              id="add-customer-last"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="add-customer-email"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Email (optional)
          </label>
          <input
            id="add-customer-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <div>
          <label
            htmlFor="add-customer-phone"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Phone (optional)
          </label>
          <input
            id="add-customer-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!firstName.trim() || !lastName.trim() || busy}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
            {busy ? 'Adding…' : 'Add customer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function Customers() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void doSearch(q);
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q]);

  async function doSearch(query: string) {
    setLoading(true);
    setError(null);
    try {
      const results = await searchCustomers(query || undefined);
      setCustomers(results);
      setTotal(results.length);
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Search failed');
    } finally {
      setLoading(false);
    }
  }

  const capped = customers.length >= 100;

  return (
    <div className="mx-auto max-w-4xl px-6 py-10 md:px-10">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-medium tracking-tight">Customers</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3.5 py-2 text-sm font-medium text-foreground hover:bg-secondary press"
          >
            <Plus className="size-4" strokeWidth={2} />
            Quick add
          </button>
          <Link
            to="/customers/new"
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground press"
          >
            <ExternalLink className="size-4" strokeWidth={2} />
            Full form
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          strokeWidth={1.75}
        />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email…"
          className="h-10 w-full rounded-md border border-border bg-surface pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          aria-label="Search customers"
        />
      </div>

      {/* Count cap banner */}
      {capped && !loading && (
        <p className="mb-4 text-sm text-muted-foreground">
          Showing <span className="num font-medium">100</span> results — refine your search to
          narrow down.
        </p>
      )}

      {/* State: loading */}
      {loading && <div className="py-12 text-center text-sm text-muted-foreground">Searching…</div>}

      {/* State: error */}
      {error && !loading && (
        <div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* State: empty (no search yet / no results) */}
      {!loading && !error && customers.length === 0 && (
        <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
          <Users
            className="mx-auto mb-3 size-8 text-muted-foreground"
            strokeWidth={1.5}
            aria-hidden
          />
          <p className="mb-1 text-sm font-medium">
            {q ? 'No customers found' : 'No customers yet'}
          </p>
          <p className="mb-4 text-sm text-muted-foreground">
            {q ? 'Try a different name or email.' : 'Add your first customer to get started.'}
          </p>
          {!q && (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 press"
            >
              <Plus className="size-4" strokeWidth={2} />
              Add customer
            </button>
          )}
        </div>
      )}

      {/* Results table */}
      {!loading && !error && customers.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                <th className="py-2.5 px-4 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Name
                </th>
                <th className="py-2.5 px-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Email
                </th>
                <th className="py-2.5 px-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Phone
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {customers.map((c) => (
                <tr key={c.id} className="hover:bg-secondary/30">
                  <td className="py-3 pl-4 pr-3">
                    <Link
                      to={`/customers/${c.id}`}
                      className="font-medium text-foreground hover:text-primary hover:underline"
                    >
                      {c.firstName} {c.lastName}
                    </Link>
                  </td>
                  <td className="py-3 px-3 text-sm text-muted-foreground">
                    {c.email ?? <span className="italic opacity-50">—</span>}
                  </td>
                  <td className="num py-3 px-3 text-sm text-muted-foreground">
                    {formatPhone(c.phone) || <span className="italic opacity-50">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {total !== null && !capped && (
            <p className="px-4 py-2.5 text-xs text-muted-foreground">
              <span className="num">{total}</span> {total === 1 ? 'customer' : 'customers'}
            </p>
          )}
        </div>
      )}

      {showAdd && (
        <AddCustomerModal
          onClose={() => setShowAdd(false)}
          onCreated={(id) => navigate(`/customers/${id}`)}
        />
      )}
    </div>
  );
}
